import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type SidebarContextType = {
  isOpen: boolean;
  setIsOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  // Keep collapsed/mobileOpen for backwards compatibility
  collapsed: boolean;
  setCollapsed: (v: boolean | ((prev: boolean) => boolean)) => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const SIDEBAR_OPEN_KEY = 'sidebar_open';

export function SidebarProvider({ children }: { children: ReactNode }) {
  // Check localStorage for user preference. Default to false so it never pops open on reload unless user left it open.
  const [isOpen, setIsOpenState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_OPEN_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
    } catch {}
    return false;
  });

  const setIsOpen = (v: boolean | ((prev: boolean) => boolean)) => {
    setIsOpenState(prev => {
      const next = typeof v === 'function' ? v(prev) : v;
      try {
        localStorage.setItem(SIDEBAR_OPEN_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Keep collapsed state if user wants compact view
  const [collapsed, setCollapsed] = useState(false);

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        setIsOpen,
        collapsed,
        setCollapsed,
        mobileOpen: isOpen,
        setMobileOpen: setIsOpen,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
}

export default SidebarContext;
